import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArchive, FiChevronLeft, FiChevronRight, FiClock, FiCreditCard, FiDollarSign,
  FiMail, FiMinus, FiPlus, FiPrinter, FiRefreshCw, FiSearch, FiShoppingBag,
  FiSlash, FiTrash2, FiUserPlus, FiX, FiZap,
} from 'react-icons/fi';
import api, { STORAGE_URL } from '../../api/axios';
import { DashLang } from '../../utils/dashboardTranslations';
import './PointOfSale.css';

type CartItem = { product: any; quantity: number; notes?: string };
type Payment = { method: string; amount: number; reference?: string };

const copy = {
  en: {
    title: 'Point of Sale', terminal: 'Register', sales: 'Sales history', search: 'Search name, SKU, or scan barcode…',
    all: 'All', cart: 'Current sale', empty: 'Scan or tap a product to start', subtotal: 'Subtotal', discount: 'Discount',
    tax: 'Tax', total: 'Total', checkout: 'Charge', customer: 'Customer', walkin: 'Walk-in customer', addCustomer: 'Quick add customer',
    name: 'Name', phone: 'Phone', email: 'Email', save: 'Save customer', cancel: 'Cancel', branch: 'Branch',
    openShift: 'Open register', openingCash: 'Opening cash', begin: 'Start selling', closeShift: 'Close shift',
    closingCash: 'Counted cash', expected: 'Expected cash', close: 'Close register', payment: 'Payment', cash: 'Cash', card: 'Card',
    knet: 'KNET', other: 'Other', amount: 'Amount', tendered: 'Cash tendered', complete: 'Complete sale',
    print: 'Print receipt', send: 'Email receipt', newSale: 'New sale', receipt: 'Receipt', cashier: 'Cashier',
    noStock: 'Out of stock', held: 'Hold sale', resume: 'Resume held sale', clear: 'Clear', today: 'Today',
    noSales: 'No POS sales yet', paid: 'Paid', refunded: 'Refunded', qty: 'Qty', change: 'Change', thankyou: 'Thank you!',
    cancelSale: 'Cancel sale', refundSale: 'Refund sale', cancelled: 'Cancelled',
    selectBranch: 'No active POS branch is assigned to this user. Ask the store owner to update Team settings.', receiptEmail: 'Receipt email', sendNow: 'Send now', loading: 'Loading register…',
  },
  ar: {
    title: 'نقطة البيع', terminal: 'الكاشير', sales: 'سجل المبيعات', search: 'ابحث بالاسم أو الرمز أو امسح الباركود…',
    all: 'الكل', cart: 'البيع الحالي', empty: 'امسح أو اختر منتجاً للبدء', subtotal: 'المجموع الفرعي', discount: 'الخصم',
    tax: 'الضريبة', total: 'الإجمالي', checkout: 'تحصيل', customer: 'العميل', walkin: 'عميل نقدي', addCustomer: 'إضافة عميل سريعاً',
    name: 'الاسم', phone: 'الهاتف', email: 'البريد الإلكتروني', save: 'حفظ العميل', cancel: 'إلغاء', branch: 'الفرع',
    openShift: 'فتح الصندوق', openingCash: 'الرصيد الافتتاحي', begin: 'بدء البيع', closeShift: 'إغلاق الوردية',
    closingCash: 'النقد المعدود', expected: 'النقد المتوقع', close: 'إغلاق الصندوق', payment: 'الدفع', cash: 'نقدي', card: 'بطاقة',
    knet: 'كي نت', other: 'أخرى', amount: 'المبلغ', tendered: 'المبلغ المستلم', complete: 'إتمام البيع',
    print: 'طباعة الإيصال', send: 'إرسال الإيصال', newSale: 'بيع جديد', receipt: 'إيصال', cashier: 'الكاشير',
    noStock: 'نفد المخزون', held: 'تعليق البيع', resume: 'استرجاع البيع المعلق', clear: 'مسح', today: 'اليوم',
    noSales: 'لا توجد مبيعات نقطة بيع', paid: 'مدفوع', refunded: 'مسترجع', qty: 'الكمية', change: 'الباقي', thankyou: 'شكراً لك!',
    cancelSale: 'إلغاء البيع', refundSale: 'استرجاع البيع', cancelled: 'ملغي',
    selectBranch: 'لا يوجد فرع POS نشط مخصص لهذا المستخدم. اطلب من مالك المتجر تحديث إعدادات المستخدمين.', receiptEmail: 'بريد الإيصال', sendNow: 'إرسال الآن', loading: 'جاري تحميل الكاشير…',
  },
};

export default function PointOfSale() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const t = copy[lang];
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [session, setSession] = useState<any>(null);
  const [openingCash, setOpeningCash] = useState('0');
  const [closingCash, setClosingCash] = useState('');
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<number | 'all'>('all');
  const [customer, setCustomer] = useState<any>(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [showCustomer, setShowCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '', email: '' });
  const [discount, setDiscount] = useState('0');
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountTendered, setAmountTendered] = useState('');
  const [processing, setProcessing] = useState(false);
  const [receipt, setReceipt] = useState<any>(null);
  const [tab, setTab] = useState<'terminal' | 'sales'>('terminal');
  const [sales, setSales] = useState<any[]>([]);
  const [emailAddress, setEmailAddress] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const load = async (requestedBranch?: number | '') => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const suffix = requestedBranch ? `?branch_id=${requestedBranch}` : '';
      const res = await api.get(`/stores/${store.id}/pos/bootstrap${suffix}`);
      setData(res.data);
      setBranchId(res.data.selected_branch_id || '');
      setSession(res.data.session);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Unable to load POS');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [store?.id]);
  useEffect(() => { searchRef.current?.focus(); }, [tab, receipt, showCheckout]);

  const subtotal = useMemo(() => cart.reduce((sum, item) => sum + Number(item.product.price) * item.quantity, 0), [cart]);
  const discountValue = Math.min(Number(discount) || 0, subtotal);
  const tax = (subtotal - discountValue) * (Number(data?.store?.pos_tax_rate) || 0) / 100;
  const total = Math.max(0, subtotal - discountValue + tax);
  const currency = data?.store?.currency_symbol || data?.store?.currency || 'KWD';
  const money = (n: number | string) => `${Number(n || 0).toFixed(3)} ${currency}`;

  const products = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.products || []).filter((p: any) => {
      const categoryMatch = category === 'all' || p.category_id === category;
      const searchMatch = !q || [p.name, p.name_ar, p.sku].some((v) => String(v || '').toLowerCase().includes(q));
      return categoryMatch && searchMatch;
    });
  }, [data, query, category]);

  const addProduct = (product: any) => {
    if (!product.is_available || (product.track_stock && product.stock !== null && Number(product.stock) <= 0)) return;
    setCart((current) => {
      const found = current.find((x) => x.product.id === product.id);
      const nextQty = (found?.quantity || 0) + 1;
      if (product.track_stock && product.stock !== null && nextQty > Number(product.stock)) {
        toast.warn(t.noStock); return current;
      }
      return found ? current.map((x) => x.product.id === product.id ? { ...x, quantity: nextQty } : x)
        : [...current, { product, quantity: 1 }];
    });
    setQuery('');
  };

  const updateQty = (id: number, delta: number) => setCart((current) => current.flatMap((item) => {
    if (item.product.id !== id) return [item];
    const quantity = item.quantity + delta;
    if (quantity <= 0) return [];
    if (item.product.track_stock && item.product.stock !== null && quantity > Number(item.product.stock)) return [item];
    return [{ ...item, quantity }];
  }));

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const exact = (data?.products || []).find((p: any) => p.sku && p.sku.toLowerCase() === query.trim().toLowerCase());
    if (exact) addProduct(exact);
    else if (products.length === 1) addProduct(products[0]);
  };

  const switchBranch = async (id: number) => {
    if (cart.length && !window.confirm(lang === 'ar' ? 'سيتم مسح السلة عند تغيير الفرع. متابعة؟' : 'Changing branch clears the cart. Continue?')) return;
    setCart([]); setCustomer(null); setReceipt(null); await load(id);
  };

  const openSession = async () => {
    if (!branchId) return;
    try {
      const res = await api.post(`/stores/${store.id}/pos/sessions`, { branch_id: branchId, opening_cash: Number(openingCash) || 0 });
      setSession(res.data); toast.success(lang === 'ar' ? 'تم فتح الصندوق' : 'Register opened');
    } catch (err: any) { toast.error(err.response?.data?.message || 'Unable to open register'); }
  };

  const closeSession = async () => {
    if (!session) return;
    try {
      const res = await api.post(`/stores/${store.id}/pos/sessions/${session.id}/close`, { closing_cash: Number(closingCash) || 0 });
      toast.success(`${t.expected}: ${money(res.data.expected_cash)} · ${lang === 'ar' ? 'الفرق' : 'Difference'}: ${money(res.data.cash_difference)}`);
      setSession(null); setShowCloseShift(false); setClosingCash('');
    } catch (err: any) { toast.error(err.response?.data?.message || 'Unable to close register'); }
  };

  useEffect(() => {
    if (!customerQuery.trim() || !store?.id) { setCustomerResults([]); return; }
    const timer = window.setTimeout(() => api.get(`/stores/${store.id}/pos/customers?search=${encodeURIComponent(customerQuery)}`)
      .then((r) => setCustomerResults(r.data)).catch(() => {}), 250);
    return () => window.clearTimeout(timer);
  }, [customerQuery, store?.id]);

  const addCustomer = async () => {
    try {
      const res = await api.post(`/stores/${store.id}/pos/customers`, customerForm);
      setCustomer(res.data); setEmailAddress(res.data.email || ''); setShowCustomer(false);
      setCustomerForm({ name: '', phone: '', email: '' });
    } catch (err: any) { toast.error(err.response?.data?.message || 'Unable to save customer'); }
  };

  const holdSale = () => {
    if (!cart.length) return;
    localStorage.setItem(`cartz_pos_hold_${store.id}_${branchId}`, JSON.stringify({ cart, customer, discount, at: Date.now() }));
    setCart([]); setCustomer(null); setDiscount('0'); toast.success(lang === 'ar' ? 'تم تعليق البيع' : 'Sale held');
  };

  const resumeSale = () => {
    const key = `cartz_pos_hold_${store.id}_${branchId}`;
    const value = localStorage.getItem(key); if (!value) return;
    try { const held = JSON.parse(value); setCart(held.cart || []); setCustomer(held.customer); setDiscount(held.discount || '0'); localStorage.removeItem(key); }
    catch { localStorage.removeItem(key); }
  };

  const completeSale = async () => {
    if (!session || !cart.length) return;
    setProcessing(true);
    const payments: Payment[] = [{ method: paymentMethod, amount: total }];
    try {
      const res = await api.post(`/stores/${store.id}/pos/checkout`, {
        branch_id: branchId, session_id: session.id, customer_id: customer?.id,
        customer_name: customer?.name, customer_phone: customer?.phone, customer_email: customer?.email,
        items: cart.map((x) => ({ product_id: x.product.id, quantity: x.quantity, notes: x.notes })),
        discount: discountValue, payments, amount_tendered: paymentMethod === 'cash' ? Number(amountTendered || total) : undefined,
        receipt_language: lang, email_receipt: !!customer?.email,
      });
      setReceipt(res.data); setEmailAddress(res.data.order.customer_email || ''); setShowCheckout(false);
      setCart([]); setCustomer(null); setDiscount('0'); setAmountTendered(''); await load(branchId);
      toast.success(lang === 'ar' ? 'تمت عملية البيع' : 'Sale completed');
    } catch (err: any) { toast.error(err.response?.data?.message || 'Checkout failed'); }
    finally { setProcessing(false); }
  };

  const loadSales = async () => {
    setTab('sales');
    try { const res = await api.get(`/stores/${store.id}/pos/sales?branch_id=${branchId}&per_page=50`); setSales(res.data.data || []); }
    catch (err: any) { toast.error(err.response?.data?.message || 'Unable to load sales'); }
  };

  const openReceipt = async (sale: any) => {
    try { const res = await api.get(`/stores/${store.id}/pos/sales/${sale.id}`); setReceipt(res.data); setEmailAddress(res.data.order.customer_email || ''); }
    catch { toast.error('Unable to load receipt'); }
  };

  const emailReceipt = async () => {
    if (!receipt?.order?.id || !emailAddress) return;
    try { await api.post(`/stores/${store.id}/pos/sales/${receipt.order.id}/email`, { email: emailAddress, lang }); toast.success(lang === 'ar' ? 'تم إرسال الإيصال' : 'Receipt sent'); }
    catch (err: any) { toast.error(err.response?.data?.message || 'Unable to send receipt'); }
  };

  const runSaleAction = async (action: 'cancel' | 'refund') => {
    if (!receipt?.order?.id) return;
    const reason = window.prompt(action === 'refund'
      ? (lang === 'ar' ? 'أدخل سبب استرجاع البيع:' : 'Enter the refund reason:')
      : (lang === 'ar' ? 'أدخل سبب إلغاء البيع:' : 'Enter the cancellation reason:'))?.trim();
    if (!reason) return;
    try {
      const res = await api.post(`/stores/${store.id}/pos/sales/${receipt.order.id}/${action}`, { reason });
      const updated = res.data;
      setReceipt({ ...receipt, order: updated });
      setSales((current) => current.map((sale) => sale.id === updated.id ? { ...sale, ...updated } : sale));
      await load(branchId);
      toast.success(action === 'refund'
        ? (lang === 'ar' ? 'تم استرجاع البيع وإعادة المخزون' : 'Sale refunded and stock restored')
        : (lang === 'ar' ? 'تم إلغاء البيع وإعادة المخزون' : 'Sale cancelled and stock restored'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || (lang === 'ar' ? 'تعذر تنفيذ العملية' : 'Unable to complete this action'));
    }
  };

  const printReceipt = () => window.print();

  if (loading && !data) return <div className="pos-loading"><FiRefreshCw className="spin" /> {t.loading}</div>;
  if (!data) return <div className="card pos-unavailable"><FiZap /><h2>{t.title}</h2><p>{lang === 'ar' ? 'هذه الميزة غير متاحة في باقتك الحالية.' : 'This feature is not available in your current plan.'}</p></div>;

  return (
    <div className="pos-shell" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="pos-topbar">
        <div><h1>{t.title}</h1><span className={`pos-session-dot ${session ? 'open' : ''}`} /> {session ? (lang === 'ar' ? 'الصندوق مفتوح' : 'Register open') : (lang === 'ar' ? 'الصندوق مغلق' : 'Register closed')}</div>
        <div className="pos-top-actions">
          <select value={branchId} onChange={(e) => switchBranch(Number(e.target.value))} aria-label={t.branch}>
            {(data.branches || []).map((b: any) => <option key={b.id} value={b.id}>{lang === 'ar' ? b.name_ar || b.name : b.name}</option>)}
          </select>
          <button className={tab === 'terminal' ? 'active' : ''} onClick={() => setTab('terminal')}><FiShoppingBag /> {t.terminal}</button>
          <button className={tab === 'sales' ? 'active' : ''} onClick={loadSales}><FiClock /> {t.sales}</button>
          {session && <button className="muted" onClick={() => setShowCloseShift(true)}><FiArchive /> {t.closeShift}</button>}
        </div>
      </div>

      {!branchId ? <div className="pos-empty-panel">{t.selectBranch}</div> : !session ? (
        <div className="pos-open-card">
          <div className="pos-open-icon"><FiDollarSign /></div><h2>{t.openShift}</h2><p>{lang === 'ar' ? 'أدخل رصيد درج النقدية قبل أول عملية بيع.' : 'Enter the cash drawer balance before the first sale.'}</p>
          <label>{t.openingCash}</label><input type="number" min="0" step="0.001" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} />
          <button onClick={openSession}>{t.begin}</button>
        </div>
      ) : tab === 'sales' ? (
        <div className="pos-sales-panel">
          <div className="pos-sales-heading"><h2>{t.sales}</h2><button onClick={loadSales}><FiRefreshCw /></button></div>
          {sales.length === 0 ? <div className="pos-empty-panel">{t.noSales}</div> : <div className="pos-sales-list">
            {sales.map((sale) => <button key={sale.id} className="pos-sale-row" onClick={() => openReceipt(sale)}>
              <span><strong>{sale.order_number}</strong><small>{new Date(sale.created_at).toLocaleString(lang === 'ar' ? 'ar-KW' : 'en-KW')}</small></span>
              <span><strong>{money(sale.total)}</strong><small className={sale.payment_status}>{sale.payment_status === 'refunded' ? t.refunded : sale.status === 'cancelled' ? t.cancelled : t.paid}</small></span>
              {lang === 'ar' ? <FiChevronLeft /> : <FiChevronRight />}
            </button>)}
          </div>}
        </div>
      ) : (
        <div className="pos-workspace">
          <section className="pos-catalog">
            <div className="pos-search"><FiSearch /><input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleSearchKey} placeholder={t.search} /><kbd>↵</kbd></div>
            <div className="pos-categories"><button className={category === 'all' ? 'active' : ''} onClick={() => setCategory('all')}>{t.all}</button>{data.categories.map((c: any) => <button key={c.id} className={category === c.id ? 'active' : ''} onClick={() => setCategory(c.id)}>{lang === 'ar' ? c.name_ar || c.name : c.name}</button>)}</div>
            <div className="pos-products">{products.map((product: any) => {
              const unavailable = !product.is_available || (product.track_stock && product.stock !== null && Number(product.stock) <= 0);
              return <button key={product.id} className={`pos-product ${unavailable ? 'unavailable' : ''}`} onClick={() => addProduct(product)} disabled={unavailable}>
                <div className="pos-product-image">{product.image ? <img src={`${STORAGE_URL}/${product.image}`} alt="" /> : <FiShoppingBag />}{unavailable && <span>{t.noStock}</span>}</div>
                <strong>{lang === 'ar' ? product.name_ar || product.name : product.name}</strong><small>{product.sku || '—'}</small>
                <div><b>{money(product.price)}</b>{product.track_stock && product.stock !== null && <em>{product.stock}</em>}</div>
              </button>;
            })}</div>
          </section>

          <aside className="pos-cart">
            <div className="pos-cart-heading"><div><h2>{t.cart}</h2><span>{cart.reduce((n, x) => n + x.quantity, 0)} {t.qty}</span></div>{cart.length > 0 && <button onClick={() => setCart([])}><FiTrash2 /> {t.clear}</button>}</div>
            <div className="pos-customer-picker">
              <FiSearch /><input value={customer ? customer.name : customerQuery} onChange={(e) => { setCustomer(null); setCustomerQuery(e.target.value); }} placeholder={t.walkin} />
              {customer && <button onClick={() => { setCustomer(null); setCustomerQuery(''); }}><FiX /></button>}
              <button className="add" onClick={() => setShowCustomer(true)} title={t.addCustomer}><FiUserPlus /></button>
              {customerResults.length > 0 && !customer && <div className="pos-customer-results">{customerResults.map((c) => <button key={c.id} onClick={() => { setCustomer(c); setCustomerQuery(''); setCustomerResults([]); setEmailAddress(c.email || ''); }}><strong>{c.name}</strong><small>{c.phone || c.email}</small></button>)}</div>}
            </div>
            <div className="pos-cart-items">{cart.length === 0 ? <div className="pos-cart-empty"><FiShoppingBag /><p>{t.empty}</p></div> : cart.map((item) => <div className="pos-cart-item" key={item.product.id}>
              <div className="pos-cart-line"><strong>{lang === 'ar' ? item.product.name_ar || item.product.name : item.product.name}</strong><b>{money(Number(item.product.price) * item.quantity)}</b></div>
              <div className="pos-cart-controls"><span>{money(item.product.price)}</span><div><button onClick={() => updateQty(item.product.id, -1)}><FiMinus /></button><strong>{item.quantity}</strong><button onClick={() => updateQty(item.product.id, 1)}><FiPlus /></button></div></div>
            </div>)}</div>
            <div className="pos-totals">
              <div><span>{t.subtotal}</span><b>{money(subtotal)}</b></div>
              <div><span>{t.discount}</span><label><input type="number" min="0" max={subtotal} step="0.001" value={discount} onChange={(e) => setDiscount(e.target.value)} /> {currency}</label></div>
              {Number(data.store.pos_tax_rate) > 0 && <div><span>{t.tax} ({data.store.pos_tax_rate}%)</span><b>{money(tax)}</b></div>}
              <div className="grand"><span>{t.total}</span><b>{money(total)}</b></div>
            </div>
            <div className="pos-cart-actions"><button className="secondary" onClick={holdSale} disabled={!cart.length}>{t.held}</button><button className="secondary" onClick={resumeSale}>{t.resume}</button></div>
            <button className="pos-charge" onClick={() => { setAmountTendered(total.toFixed(3)); setShowCheckout(true); }} disabled={!cart.length}><FiZap /> {t.checkout} · {money(total)}</button>
          </aside>
        </div>
      )}

      {showCustomer && <Modal onClose={() => setShowCustomer(false)}><h2>{t.addCustomer}</h2><label>{t.name} *</label><input value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} autoFocus /><label>{t.phone}</label><input value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} dir="ltr" /><label>{t.email}</label><input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} dir="ltr" /><div className="pos-modal-actions"><button className="secondary" onClick={() => setShowCustomer(false)}>{t.cancel}</button><button onClick={addCustomer} disabled={!customerForm.name || (!customerForm.phone && !customerForm.email)}>{t.save}</button></div></Modal>}

      {showCheckout && <Modal onClose={() => setShowCheckout(false)} wide><div className="pos-payment-title"><span><FiCreditCard /></span><div><h2>{t.payment}</h2><p>{t.total}: <strong>{money(total)}</strong></p></div></div><div className="pos-payment-methods">{['cash', 'card', 'knet', 'other'].map((method) => <button key={method} className={paymentMethod === method ? 'active' : ''} onClick={() => setPaymentMethod(method)}>{method === 'cash' ? <FiDollarSign /> : <FiCreditCard />}{(t as any)[method]}</button>)}</div>{paymentMethod === 'cash' && <><label>{t.tendered}</label><input className="pos-big-input" type="number" min={total} step="0.001" value={amountTendered} onChange={(e) => setAmountTendered(e.target.value)} autoFocus /><div className="pos-change"><span>{t.change}</span><strong>{money(Math.max(0, Number(amountTendered || 0) - total))}</strong></div></>}<button className="pos-complete" onClick={completeSale} disabled={processing || (paymentMethod === 'cash' && Number(amountTendered) < total)}>{processing ? t.loading : `${t.complete} · ${money(total)}`}</button></Modal>}

      {showCloseShift && <Modal onClose={() => setShowCloseShift(false)}><h2>{t.closeShift}</h2><label>{t.closingCash}</label><input className="pos-big-input" type="number" min="0" step="0.001" value={closingCash} onChange={(e) => setClosingCash(e.target.value)} autoFocus /><div className="pos-modal-actions"><button className="secondary" onClick={() => setShowCloseShift(false)}>{t.cancel}</button><button onClick={closeSession}>{t.close}</button></div></Modal>}

      {receipt && <div className="pos-receipt-overlay"><div className="pos-receipt-dialog"><div className="pos-receipt-actions no-print"><button onClick={() => setReceipt(null)}><FiX /></button><div>{data.permissions?.cancel && receipt.order.status !== 'cancelled' && <button onClick={() => runSaleAction('cancel')}><FiSlash /> {t.cancelSale}</button>}{data.permissions?.refund && receipt.order.payment_status === 'paid' && <button onClick={() => runSaleAction('refund')}><FiRefreshCw /> {t.refundSale}</button>}<button onClick={printReceipt}><FiPrinter /> {t.print}</button><button className="primary" onClick={() => { setReceipt(null); setTab('terminal'); }}><FiPlus /> {t.newSale}</button></div></div><ReceiptView receipt={receipt} lang={lang} money={money} t={t} /><div className="pos-email-receipt no-print"><label>{t.receiptEmail}</label><div><FiMail /><input type="email" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} placeholder="customer@example.com" dir="ltr" /><button onClick={emailReceipt} disabled={!emailAddress}>{t.sendNow}</button></div></div></div></div>}
    </div>
  );
}

function Modal({ children, onClose, wide = false }: { children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return <div className="pos-modal-backdrop" onMouseDown={onClose}><div className={`pos-modal ${wide ? 'wide' : ''}`} onMouseDown={(e) => e.stopPropagation()}>{children}</div></div>;
}

function ReceiptView({ receipt, lang, money, t }: any) {
  const { order, store } = receipt;
  return <div id="pos-print-receipt" className={`pos-paper size-${store.pos_receipt_size || '80mm'}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
    <header>{store.logo && <img src={`${STORAGE_URL}/${store.logo}`} alt="" />}<h2>{lang === 'ar' ? store.name_ar || store.name : store.name}</h2>{store.address && <p>{store.address}</p>}{store.phone && <p>{store.phone}</p>}</header>
    <div className="pos-receipt-meta"><span>{order.order_number}</span><span>{new Date(order.created_at).toLocaleString(lang === 'ar' ? 'ar-KW' : 'en-KW')}</span>{order.branch && <span>{lang === 'ar' ? order.branch.name_ar || order.branch.name : order.branch.name}</span>}{order.cashier && <span>{t.cashier}: {order.cashier.first_name} {order.cashier.last_name}</span>}{order.customer_name && order.customer_name !== 'Walk-in customer' && <span>{t.customer}: {order.customer_name}</span>}</div>
    <table><thead><tr><th>{lang === 'ar' ? 'الصنف' : 'Item'}</th><th>{t.qty}</th><th>{t.total}</th></tr></thead><tbody>{order.items.map((item: any) => <tr key={item.id}><td>{item.product_name}</td><td>{item.quantity}</td><td>{money(item.total)}</td></tr>)}</tbody></table>
    <div className="pos-receipt-totals"><div><span>{t.subtotal}</span><b>{money(order.subtotal)}</b></div>{Number(order.discount) > 0 && <div><span>{t.discount}</span><b>-{money(order.discount)}</b></div>}{Number(order.tax) > 0 && <div><span>{t.tax}</span><b>{money(order.tax)}</b></div>}<div className="grand"><span>{t.total}</span><b>{money(order.total)}</b></div>{Number(order.change_due) > 0 && <div><span>{t.change}</span><b>{money(order.change_due)}</b></div>}</div>
    <div className="pos-receipt-payments">{(order.pos_payments || []).map((p: any) => <span key={p.id}>{(t as any)[p.method] || p.method}: {money(p.amount)}</span>)}</div>
    <footer><p>{lang === 'ar' ? store.pos_receipt_footer_ar || t.thankyou : store.pos_receipt_footer || t.thankyou}</p><small>Powered by Cartz</small></footer>
  </div>;
}
